import { getGuideTourRuntimeConfig } from '../config'
import { getGuideTourStepRoute } from '../routing/utils'
import type { GuideTourDefinition } from '../types'
import {
  countAvailableGuideTourSteps,
  getAvailableGuideTourStepNumber,
} from '../utils/tourRuntime'
import type {
  GuideTourInProgressNotification,
  GuideTourNotificationInteractionAction,
  GuideTourNotificationInteractionEvent,
  GuideTourStepInteractionAction,
} from './types'

export type GuideTourStepInteractionDisplay = {
  currentStep: number
  totalStep: number
}

export const buildGuideTourNotificationInteractionEvent = (
  action: GuideTourNotificationInteractionAction,
  notificationItem: GuideTourInProgressNotification | null,
  notificationItems: readonly GuideTourInProgressNotification[]
): GuideTourNotificationInteractionEvent => ({
  action,
  notificationItem,
  notificationItems,
  notificationCount: notificationItems.length,
})

/** Ensures `tourId` / `tourVersion` are set whenever they can be derived from the payload. */
export const enrichGuideTourInteractionEvent = (
  event: GuideTourNotificationInteractionEvent
): GuideTourNotificationInteractionEvent => {
  const tourId =
    event.tourId ?? event.tour?.id ?? event.notificationItem?.tourId
  const tourVersion =
    event.tourVersion ??
    event.tour?.version ??
    event.notificationItem?.tourVersion

  if (tourId === undefined && tourVersion === undefined) {
    return event
  }

  return {
    ...event,
    ...(tourId !== undefined ? { tourId } : {}),
    ...(tourVersion !== undefined ? { tourVersion } : {}),
  }
}

export const resolveGuideTourOnInteraction = (
  override?: (event: GuideTourNotificationInteractionEvent) => void
): ((event: GuideTourNotificationInteractionEvent) => void) | undefined =>
  override ?? getGuideTourRuntimeConfig()?.onInteraction

export const notifyGuideTourInteraction = (
  event: GuideTourNotificationInteractionEvent,
  override?: (event: GuideTourNotificationInteractionEvent) => void
): void => {
  const onInteraction = resolveGuideTourOnInteraction(override)

  if (!onInteraction) {
    return
  }

  try {
    onInteraction(enrichGuideTourInteractionEvent(event))
  } catch {
    // Fire-and-forget — host analytics must not block the tour UX.
  }
}

export type GuideTourStepInteractionOptions = {
  autoStart?: boolean
}

export const notifyGuideTourStepInteraction = (
  action: GuideTourStepInteractionAction,
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string,
  display?: GuideTourStepInteractionDisplay,
  options?: GuideTourStepInteractionOptions
): void => {
  const step = tour.steps[stepIndex]

  notifyGuideTourInteraction({
    action,
    currentStep:
      display?.currentStep ?? getAvailableGuideTourStepNumber(tour, stepIndex),
    totalStep: display?.totalStep ?? countAvailableGuideTourSteps(tour),
    pathname,
    route:
      (step && getGuideTourStepRoute(tour, step)) || tour.route || pathname,
    tour,
    step,
    ...(options?.autoStart !== undefined
      ? { autoStart: options.autoStart }
      : {}),
  })
}

export const notifyGuideTourUnavailableOnPage = (
  tour: GuideTourDefinition,
  pathname: string
): void => {
  notifyGuideTourInteraction({
    action: 'tour-unavailable',
    pathname,
    route: tour.route ?? null,
    tour,
  })
}
